/**
 * SampleRegistry: built-in manifest (/samples/manifest.json) merged with the signed-in user's /api/samples,
 * decoded AudioBuffers cached per id, seamless loops verified/baked in the browser when the manifest cannot be
 * trusted (missing loop points, decoder padding, context sample rate ≠ file rate), and local registration of
 * recorded/uploaded buffers.
 */
import {
  SampleMetaSchema, SamplesListDto, findLoop, bakeLoop, mixToMono, estimateF0, midiToHz, hzToMidi,
  type SampleMeta,
} from '@tonist/shared';
import type { PreparedUserBuffer, SampleRegistry } from './types';
import { BASE_URL } from './stretch';

export interface PreparedSample {
  id: string;
  buffer: AudioBuffer;
  /** seconds */
  loopStart: number;
  loopEnd: number;
  rootHz: number;
  rootMidi: number;
}

const GUARD_SEC = 0.25;

export function decodeAudio(ctx: BaseAudioContext, data: ArrayBuffer): Promise<AudioBuffer> {
  return new Promise<AudioBuffer>((resolve, reject) => {
    let p: Promise<AudioBuffer> | undefined;
    try {
      p = ctx.decodeAudioData(data, resolve, reject) as Promise<AudioBuffer> | undefined;
    } catch (e) { reject(e); return; }
    if (p && typeof p.then === 'function') p.then(resolve, reject);
  });
}

/** True when the manifest loop points cannot be used as-is on the decoded buffer. */
export function needsLoopSearch(meta: Pick<SampleMeta, 'loopStart' | 'loopEnd' | 'sampleRate' | 'expectedFrames'>, decodedLength: number, decodedRate: number): boolean {
  if (!(meta.loopEnd > meta.loopStart)) return true;
  const ratio = decodedRate / meta.sampleRate;
  if (Math.abs(ratio - 1) > 1e-6) return true;
  if (meta.loopEnd > decodedLength) return true;
  if (meta.expectedFrames && Math.abs(decodedLength - meta.expectedFrames * ratio) > 64) return true;
  return false;
}

/** Find + bake a seamless loop on raw channel data. Returns new channel arrays and loop points in frames. */
export function analyseLoop(channels: Float32Array[], sampleRate: number, startHintSec?: number): { channels: Float32Array[]; loopStart: number; loopEnd: number; rootHz: number } {
  const mono = mixToMono(channels);
  const loop = findLoop(mono, sampleRate, startHintSec !== undefined && startHintSec > 0 ? { startSec: startHintSec } : {});
  const baked = bakeLoop(channels, loop, Math.floor(GUARD_SEC * sampleRate));
  const rootHz = estimateF0(mono, sampleRate);
  return { channels: baked.channels, loopStart: baked.loopStart, loopEnd: baked.loopEnd, rootHz };
}

function channelsOf(buffer: AudioBuffer): Float32Array[] {
  const out: Float32Array[] = [];
  for (let c = 0; c < buffer.numberOfChannels; c++) out.push(buffer.getChannelData(c));
  return out;
}

function bufferFrom(ctx: BaseAudioContext, channels: Float32Array[], sampleRate: number): AudioBuffer {
  const buf = ctx.createBuffer(channels.length, channels[0]!.length, sampleRate);
  channels.forEach((ch, i) => buf.copyToChannel(ch as Float32Array<ArrayBuffer>, i));
  return buf;
}

async function fetchJson(url: string): Promise<unknown> {
  const res = await fetch(url, { credentials: 'same-origin' });
  if (!res.ok) throw new Error(`${url}: ${res.status}`);
  return res.json();
}

export class Registry implements SampleRegistry {
  private readonly metas = new Map<string, SampleMeta>();
  private readonly raw = new Map<string, ArrayBuffer>();
  private readonly local = new Map<string, AudioBuffer>();
  private readonly prepared = new Map<string, Promise<PreparedSample>>();
  private refreshing: Promise<void> | null = null;
  private refreshedOnce = false;

  constructor(private readonly getCtx: () => BaseAudioContext | null) {}

  list(): SampleMeta[] { return Array.from(this.metas.values()); }
  get(id: string): SampleMeta | undefined { return this.metas.get(id); }

  refresh(): Promise<void> {
    if (this.refreshing) return this.refreshing;
    this.refreshing = (async () => {
      try {
        const m = await fetchJson(`${BASE_URL}samples/manifest.json`) as { samples?: unknown[] };
        for (const item of m?.samples ?? []) {
          const p = SampleMetaSchema.safeParse(item);
          if (p.success) this.metas.set(p.data.id, p.data);
        }
      } catch (e) {
        console.warn('[tonist] samples manifest unavailable', e);
      }
      try {
        const r = SamplesListDto.safeParse(await fetchJson('/api/samples'));
        if (r.success) for (const s of r.data.items) this.metas.set(s.id, s);
      } catch { /* anonymous or server absent: fine */ }
      this.refreshedOnce = true;
    })().finally(() => { this.refreshing = null; });
    return this.refreshing;
  }

  /** Fetch a sample's bytes into the cache (no AudioContext needed) so decoding is immediate later. */
  async prefetch(id: string): Promise<void> {
    if (this.raw.has(id) || this.local.has(id)) return;
    if (!this.metas.has(id) && !this.refreshedOnce) await this.refresh();
    const meta = this.metas.get(id);
    if (!meta) return;
    const res = await fetch(meta.url, { credentials: 'same-origin' });
    if (!res.ok) return;
    const data = await res.arrayBuffer();
    if (!this.raw.has(id)) this.raw.set(id, data);
  }

  async buffer(id: string): Promise<AudioBuffer> {
    return (await this.preparedSample(id)).buffer;
  }

  /** One real note of a multisampled source (see SampleMeta.notes), decoded and loop-verified like the primary. */
  preparedNote(id: string, rootMidi: number): Promise<PreparedSample> {
    const key = `${id}#${rootMidi}`;
    let p = this.prepared.get(key);
    if (!p) {
      p = (async () => {
        if (!this.metas.has(id) && !this.refreshedOnce) await this.refresh();
        const meta = this.metas.get(id);
        const note = meta?.notes?.find((n) => n.rootMidi === rootMidi);
        if (!meta || !note) throw new Error(`unknown note ${rootMidi} of sample "${id}"`);
        const ctx = this.getCtx();
        if (!ctx) throw new Error('audio context not started');
        let data = this.raw.get(key);
        if (!data) {
          const res = await fetch(note.url, { credentials: 'same-origin' });
          if (!res.ok) throw new Error(`sample ${id} note ${rootMidi}: HTTP ${res.status} for ${note.url}`);
          data = await res.arrayBuffer();
          this.raw.set(key, data);
        }
        const decoded = await decodeAudio(ctx, data.slice(0));
        // the note carries its own loop points and root; reuse the primary's verification path with a note-shaped meta
        const noteMeta: SampleMeta = { ...meta, id: key, url: note.url, loopStart: note.loopStart, loopEnd: note.loopEnd, rootHz: note.rootHz, rootMidi: note.rootMidi, expectedFrames: note.expectedFrames, durationMs: note.durationMs, notes: undefined };
        return this.prepareDecoded(ctx, noteMeta, decoded);
      })().catch((e: unknown) => { this.prepared.delete(key); throw e; });
      this.prepared.set(key, p);
    }
    return p;
  }

  /** Decoded buffer plus verified loop points (seconds) and root pitch. */
  preparedSample(id: string): Promise<PreparedSample> {
    let p = this.prepared.get(id);
    if (!p) {
      p = this.prepare(id).catch((e: unknown) => { this.prepared.delete(id); throw e; });
      this.prepared.set(id, p);
    }
    return p;
  }

  private async prepare(id: string): Promise<PreparedSample> {
    if (!this.metas.has(id) && !this.refreshedOnce) await this.refresh();
    const meta = this.metas.get(id);
    if (!meta) throw new Error(`unknown sample "${id}"`);
    const ctx = this.getCtx();
    if (!ctx) throw new Error('audio context not started');
    let decoded = this.local.get(id);
    if (!decoded) {
      let data = this.raw.get(id);
      if (!data) {
        const res = await fetch(meta.url, { credentials: 'same-origin' });
        if (!res.ok) throw new Error(`sample ${id}: HTTP ${res.status} for ${meta.url}`);
        data = await res.arrayBuffer();
        this.raw.set(id, data);
      }
      decoded = await decodeAudio(ctx, data.slice(0)); // decode detaches its input; keep the original for rebuilds
    }
    return this.prepareDecoded(ctx, meta, decoded);
  }

  private prepareDecoded(ctx: BaseAudioContext, meta: SampleMeta, decoded: AudioBuffer): PreparedSample {
    const sr = decoded.sampleRate;
    let buffer = decoded;
    let loopStart: number;
    let loopEnd: number;
    let rootHz = meta.rootHz;
    if (needsLoopSearch(meta, decoded.length, sr)) {
      const ratio = sr / meta.sampleRate;
      const hint = meta.loopEnd > meta.loopStart ? (meta.loopStart * ratio) / sr : undefined;
      const a = analyseLoop(channelsOf(decoded).map((c) => c.slice()), sr, hint);
      buffer = bufferFrom(ctx, a.channels, sr);
      loopStart = a.loopStart / sr;
      loopEnd = a.loopEnd / sr;
      if (!(rootHz > 0) && a.rootHz > 0) rootHz = a.rootHz;
      meta.loopStart = a.loopStart;
      meta.loopEnd = a.loopEnd;
      meta.sampleRate = sr;
      meta.expectedFrames = buffer.length;
      meta.durationMs = (buffer.length / sr) * 1000;
    } else {
      loopStart = meta.loopStart / sr;
      loopEnd = meta.loopEnd / sr;
    }
    if (!(rootHz > 0)) rootHz = midiToHz(meta.rootMidi || 49);
    return { id: meta.id, buffer, loopStart, loopEnd, rootHz, rootMidi: hzToMidi(rootHz) };
  }

  registerLocal(meta: SampleMeta, buffer: AudioBuffer): void {
    const m: SampleMeta = { ...meta };
    if (!(m.loopEnd > m.loopStart) || m.sampleRate !== buffer.sampleRate) {
      const a = analyseLoop(channelsOf(buffer).map((c) => c.slice()), buffer.sampleRate);
      const ctx = this.getCtx();
      if (ctx) buffer = bufferFrom(ctx, a.channels, buffer.sampleRate);
      m.loopStart = a.loopStart;
      m.loopEnd = a.loopEnd;
      m.sampleRate = buffer.sampleRate;
      m.channels = buffer.numberOfChannels;
      m.expectedFrames = buffer.length;
      m.durationMs = (buffer.length / buffer.sampleRate) * 1000;
      if (!(m.rootHz > 0) && a.rootHz > 0) { m.rootHz = a.rootHz; m.rootMidi = Math.round(hzToMidi(a.rootHz)); }
    }
    if (!(m.rootHz > 0)) { m.rootHz = midiToHz(m.rootMidi || 49); }
    this.metas.set(m.id, m);
    this.local.set(m.id, buffer);
    this.prepared.delete(m.id);
  }

  /** Analyse a recorded/uploaded buffer: seamless loop baked, root pitch estimated. */
  prepareUserBuffer(buffer: AudioBuffer): PreparedUserBuffer {
    const sr = buffer.sampleRate;
    const a = analyseLoop(channelsOf(buffer).map((c) => c.slice()), sr);
    const ctx = this.getCtx();
    const out = ctx ? bufferFrom(ctx, a.channels, sr) : buffer;
    const rootHz = a.rootHz > 0 ? a.rootHz : midiToHz(49);
    return {
      buffer: out,
      meta: {
        durationMs: (out.length / sr) * 1000,
        sampleRate: sr,
        channels: out.numberOfChannels,
        loopStart: ctx ? a.loopStart : 0,
        loopEnd: ctx ? a.loopEnd : 0,
        rootHz,
        rootMidi: Math.round(hzToMidi(rootHz)),
        expectedFrames: out.length,
      },
    };
  }

  /** Drop decoded buffers (context replaced); raw bytes and local buffers are kept for re-decoding. */
  invalidate(): void {
    this.prepared.clear();
  }

  /** Free memory for a sample no loaded drone uses any more. */
  release(id: string): void {
    for (const k of Array.from(this.prepared.keys())) if (k.startsWith(`${id}#`)) { this.prepared.delete(k); this.raw.delete(k); }
    this.prepared.delete(id);
  }
}
